/* eslint-disable react/no-unused-prop-types */
import React, { memo, forwardRef, useImperativeHandle } from 'react'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControl from '@material-ui/core/FormControl'
import PropTypes from 'prop-types'

import { FormLabel, FormControlLabel } from './'
import { useBaseComponentOperations } from '../../hooks'
import { translate } from '../../multilanguage'
import { defaultColor } from './common'

export const BaseRadio = props => {
  if (props.label) {
    return (
      <FormControlLabel 
        value={props.value} 
        control={
          <Radio color={props.color || 'primary'} />
        } 
        label={typeof props.label !== 'string' ? props.label : props.noTranslate ? props.label : translate(props.label)}
      />
    )
  }

  return <Radio value={props.value} color={props.color || 'primary'} />
}

BaseRadio.propTypes = {  
  label: PropTypes.string,
  color: PropTypes.string,
  value: PropTypes.string,
  noTranslate: PropTypes.bool,
}

/**
 * Implements the Mui RadioGroup.
 *
 * @param {object} props component props
 * @returns {RadioGroup} RadioGroup component
 */
const BaseRadioGroup = memo(forwardRef((props, ref) => {
  const [value, onChange, setValue] = useBaseComponentOperations(props, 'value', props.value || props.defaultValue || (props.items.length > 0 && props.items[0].value))
  useImperativeHandle(ref, () => ({
    getValue: () => value,
    setValue: value => setValue(value)
  }))
  const radioBoxes = props.items.map((item, i) => <BaseRadio noTranslate={props.noTranslateItems} key={i} color={props.color} label={item.label} value={item.value} />)
  
  return (
    <FormControl error={props.error}>
      {props.label && <FormLabel noTranslate={props.noTranslate} color='default'>{props.label}</FormLabel>}
      <RadioGroup id={props.id} value={value} onChange={onChange}>
        {radioBoxes}
      </RadioGroup>
    </FormControl>
  )
}))

BaseRadioGroup.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   value for the radio group.
   */ 
  value: PropTypes.string,
  /**
   Initial value for the radio group. If not input, the first item in the items array is selected.
   */ 
  defaultValue: PropTypes.string,
  /**
   Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
   onChange function called when the checkbox is clicked.
   */
  onChange: PropTypes.func,
  /**
   MUI color for the radio checkboxes.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Items to display in the radio group.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  /**
   Prevents the language translation of the label and radio items labels.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the items labels.
   */
  noTranslateItems: PropTypes.bool,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
}

BaseRadioGroup.defaultProps = {
  noTranslate: false,
  color: defaultColor,
}

export default BaseRadioGroup